import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, resolveDynamicComponent as _resolveDynamicComponent, mergeProps as _mergeProps, createTextVNode as _createTextVNode, withModifiers as _withModifiers } from "vue"

import { ref, onMounted, watchEffect, toRaw, watch, getCurrentInstance, computed } from 'vue';
import { useRoute } from 'vue-router';
import { configRegistry } from '@/lib/configRegistry';
import { remoteOptionsRegistry } from '@/lib/remoteOptions';
import { useFloatFormStore } from '@/store/floatForm';
import type { FormField, FormProps } from '@/types/form';
import debounce from 'lodash.debounce';


export default /*@__PURE__*/_defineComponent({
  __name: 'EditForm',
  setup(__props) {

const loading = ref(true);
const is_saving = ref(false);

const route = useRoute();
const floatFormStore = useFloatFormStore();
const props = floatFormStore.props as FormProps;
const moduleName =floatFormStore.module;
const formTitle =floatFormStore.formTitle;
const onUpdate = props?.onUpdate;
const item = props?.item || {};

const formFields = ref<FormField[]>([]);
const steps = ref<Array<{ title?: string; description?: string; fields: FormField[] }>>([]);
const currentStep = ref(0);
const hasSteps = computed(() => steps.value.length > 0);
const allFields = computed<FormField[]>(() =>
  hasSteps.value ? steps.value.flatMap(step => step.fields) : formFields.value
);
const currentStepFields = computed<FormField[]>(() =>
  hasSteps.value ? steps.value[currentStep.value]?.fields || [] : formFields.value
);
const form = ref<Record<string, any>>({});
const formValid = ref(true);
const formRef = ref();
const visibleFields = ref<FormField[]>([]);

function useCan() {
  const instance = getCurrentInstance();
  return instance?.appContext.config.globalProperties.$can as (perm: string) => boolean;
}

const $can = useCan();

watchEffect(() => {
  visibleFields.value = currentStepFields.value
    .map((field) => {
      // === PERMISOS PRIMERO ===
      if (field.permission) {
        const hasPermission = $can(field.permission);
        if (!hasPermission) {
          if (field.permission_state === 'hidden') return null;
          if (field.permission_state === 'disabled') return { ...field, disabled: true };
        }
      }

      // === DEPENDENCIAS DE OTROS CAMPOS ===
      const val = form.value[field.dependsOn as any];
      const condition = field.dependsCondition;
      let conditionMet = true;

      if (field.dependsOn && field.dependsBehavior) {
        switch (condition?.condition) {
          case 'diferent':
            conditionMet = val !== undefined && val !== '' && val !== condition.value;
            break;
          case 'equals':
            conditionMet = val === condition.value;
            break;
          case 'includes':
            conditionMet = !JSON.stringify(val)?.toLowerCase?.().includes(condition.value);
            break;
        }

        if (field.dependsBehavior === 'hidden' && !conditionMet) return null;
        if (field.dependsBehavior === 'disabled' && !conditionMet) {
          return { ...field, disabled: true };
        }
      }

      return field;
    })
    .filter((f): f is FormField => !!f)
    .filter(f => !!f.component);
});

const debouncedFetchers = {} as any;

const getFetchParams = (field: FormField, form: any, val: string = '') => {
  let params: Record<string, any> = {};
  if (field.searchValue && val!='') params[`filter[${field.searchValue}]`] = val;
  if (typeof field.fetchParams === 'function') {
    params = { ...params, ...field.fetchParams(form) };
  }
  return params;
};

const fieldLoading = ref<Record<string, boolean>>({});


const getFieldProps = (field: FormField) => {
  const { children, component, value, transform, parse, ...rest } = field;
  const props: Record<string, any> = {
    ...rest,
    label: field.label,
    clearable: true,
    density: 'compact',
    rules: field.required ? [(v: any) => !!v || 'Campo obligatorio'] : [],
    'item-title': field.itemText || 'label',
    'item-value': field.itemValue || 'value',
    disabled: field.permission === 'disabled',
    readonly: field.readonly,
    'prepend-inner-icon': field.icon,
    items: field.options || [],
    loading: !!fieldLoading.value[field.value],
  };

  // fetch dinámico/autocomplete
  if (field.fetchOptions && remoteOptionsRegistry[field.fetchOptions]) {
    const fetchFn = remoteOptionsRegistry[field.fetchOptions as any];

    if (!debouncedFetchers[field.value]) {
      debouncedFetchers[field.value] = debounce(async (val: string) => {
        fieldLoading.value[field.value] = true;
        const params = getFetchParams(field, form.value, val);
        const results = await fetchFn(params);

        const selected = form.value[field.value];
        let options = Array.isArray(results) ? [...results] : [];
        const findSelected = (opt: any) => {
          const itemValue = typeof field.itemValue === 'function'
            ? field.itemValue(selected)
            : (selected?.[field.itemValue || 'value'] ?? selected?.id);
          return opt?.[field.itemValue || 'value'] === itemValue || opt?.id === itemValue;
        };
        if (selected && !options.find(findSelected)) {
          options = [selected, ...options];
        }
        
        field.options = Array.isArray(results) ? [...results] : [];
        fieldLoading.value[field.value] = false;
      }, 500);
    }

    props['onFocus'] = (open: boolean) => {
      if (open && (!field.options || field.options.length === 0)) {
        fieldLoading.value[field.value] = true;
        // Fetch sin search al abrir (ejemplo: muestra todo, o hazlo configurable)
        fetchFn(getFetchParams(field, form.value, '')).then(results => {

          let options = Array.isArray(results) ? [...results] : [];
          const selected = form.value[field.value];
          if (selected && !options.find(opt => opt.id === selected.id)) {
            options = [selected, ...options];
          } 

          field.options = Array.isArray(results) ? [...results] : [];
          fieldLoading.value[field.value] = false;
        });
      }
    };

    props['onUpdate:search'] = (val: string) => {
      /*if (typeof val === 'string' && val.trim().length < 2) {
        field.options = [];
        return;
      }*/
      debouncedFetchers[field.value](val);
    };
  }

  return props;
};

const flattenForm = (form: Record<string, any>, fields: FormField[]) => {
  const flatForm: Record<string, any> = { id: form.id };

  for (const field of fields) {
    const val = form[field.value];

    // 0. Campos de solo layout: procesa hijos directamente sin agregar key
    if (field.layoutOnly && field.children) {
      Object.assign(flatForm, flattenForm(form, field.children));
      continue;
    }

    // 1. Si hay transform, mergea resultado y CONTINÚA (no sigas procesando)
    if (typeof field.transform === 'function') {
      const result = field.transform(toRaw(val), form);
      if (typeof result === 'object' && !Array.isArray(result)) {
        Object.assign(flatForm, result);
      } else {
        flatForm[field.value] = result;
      }
      continue; // 👈🏼 Esto es CLAVE: no sigas procesando el campo
    }

    // 2. isJsonArray + children
    if (field.isJsonArray && field.children) {
      if (field.plainJson) {
        for (const child of field.children) {
          flatForm[child.value] = val?.[child.value] ?? null;
        }
      } else {
        flatForm[field.value] = toRaw(val);
      }
      continue;
    }

    // 3. Multiple simple (no isJsonArray)
    if (field.multiple && Array.isArray(val)) {
      flatForm[field.value] = val.map((v: any) =>
        typeof v === 'object' ? v[field.itemValue || 'value'] ?? v.id ?? null : v
      );
      continue;
    }

    // 4. Objeto simple (select/autocomplete sin transform)
    if (typeof val === 'object' && val !== null) {
      flatForm[field.value] = val[field.itemValue || 'value'] ?? val.id ?? null;
      continue;
    }

    // 5. Valor plano (si no se seteo ya por transform)
    flatForm[field.value] = val ?? null;
  }

  return flatForm;
};


const resetForm = () => {
  fieldLoading.value = {};
  const resetField = (f: FormField) => {
    if (f.layoutOnly && f.children) {
      f.children.forEach(resetField);
      return;
    }

    if (f.isJsonArray && f.plainJson && f.children) {
      const resetGroup: Record<string, any> = {};
      f.children.forEach(child => {
        form.value[child.value] = null;
        resetGroup[child.value] = null;
      });
      form.value[f.value] = resetGroup;
      return;
    }

    form.value[f.value] = null;
  };

  allFields.value.forEach(resetField);
};

const closeForm = () => {
  resetForm();
  floatFormStore.closeForm();
};

const submitForm = async () => {
  if (!formRef.value?.validate()) return;
  is_saving.value = true;
  try {
    const payload = flattenForm(form.value, allFields.value);
    payload.id = item.id;

    if (props.onSubmit) {
      props.onSubmit(payload); // <--- aquí mandas el objeto, NO el form ni el evento
    } else if (onUpdate) {
      await onUpdate(payload);
    } else {
      console.warn('[EditForm] No se proporcionó función onUpdate');
    }

    closeForm();
    is_saving.value = false;
  } catch (err) {
    is_saving.value = false;
    console.error('[EditForm] Error en onUpdate', err);
  }
};

function initFormValues(fields: FormField[], initialValues: any) {
  for (const f of fields) {
    if (f.layoutOnly && f.children) {
      initFormValues(f.children, initialValues);
      continue;
    }

    let raw = initialValues?.[f.value] ?? null;

    // Grupos tipo JSON array
    if (f.isJsonArray && f.plainJson && f.children) {
      const group: Record<string, any> = {};
      for (const child of f.children) {
        group[child.value] = initialValues?.[child.value] ?? null;
      }
      raw = group;
    }
    // Soporte para función de parseo
    if (typeof f.parse === 'function') {
      form.value[f.value] = f.parse(raw, initialValues);
    } else {
      form.value[f.value] = raw;
    }

    // fetch dinámico/autocomplete (solo para tener el label en selects)
    if (f.fetchOptions && remoteOptionsRegistry[f.fetchOptions]) {
      if (form.value[f.value] && typeof form.value[f.value] === 'object') {
        f.options = [form.value[f.value]];
      } else {
        f.options = [];
      }
    }
  }
}

onMounted(async () => {
  // Si mandas campos personalizados (reagendar u otro caso), priorízalos
  if (props?.fields && Array.isArray(props.fields)) {
    formFields.value = props.fields;

    // Carga valores iniciales de 'initialValues' (preferente), o 'item'
    const initial = props.initialValues ?? props.item ?? {};
    for (const f of formFields.value) {
      let value = initial[f.value];
      if ((f.component as any)?.name === 'VSelect' || f.component === 'VSelect') {
        if (f.fetchOptions && remoteOptionsRegistry[f.fetchOptions]) {
          const fetchFn = remoteOptionsRegistry[f.fetchOptions];
          // Llama una sola vez para cargar las opciones
          fetchFn('').then(results => {
            f.options = Array.isArray(results) ? [...results] : [];
          });
        }
      }
      if (f.isJsonArray && f.plainJson && f.children) {
        const group: Record<string, any> = {};
        for (const child of f.children) {
          group[child.value] = initial[child.value] ?? null;
        }
        value = group;
      }
      if (typeof f.parse === 'function') {
        form.value[f.value] = f.parse(value, initial);
        continue;
      }
      
      form.value[f.value] = value ?? null;
    }
    loading.value = false;
  } else {
    // ---- FLUJO NORMAL: usa la config del módulo y props.item ----
    const loader = moduleName ? configRegistry[moduleName] : false;
    if (!loader) {
      console.error(`[EditForm] Config para ${moduleName} no encontrada.`);
      return;
    }

    try {
      const mod = await loader();
      const maybeSteps = mod.EDIT_STEPS || mod.EDIT_FORM_STEPS;
      if (Array.isArray(maybeSteps) && maybeSteps.length) {
        steps.value = maybeSteps;
        formFields.value = steps.value.flatMap((s: any) => s.fields || []);
      } else {
        formFields.value = mod.EDIT_FORM || [];
      }
      const initial = props.initialValues ?? {};
      for (const f of allFields.value) {
        let raw = initial[f.value] ?? item[f.value] ?? item.attributes?.[f.value];
        //let raw = item[f.value] ?? item.attributes?.[f.value];
        if ((f.component as any)?.name === 'VSelect' || f.component === 'VSelect') {
          if (f.fetchOptions && remoteOptionsRegistry[f.fetchOptions]) {
            const fetchFn = remoteOptionsRegistry[f.fetchOptions];
            // Llama una sola vez para cargar las opciones
            fetchFn('').then(results => {
              f.options = Array.isArray(results) ? [...results] : [];
            });
          }
        }
        if (f.isJsonArray && f.plainJson && f.children) {
          const group: Record<string, any> = {};
          for (const child of f.children) {
            group[child.value] = item[child.value] ?? item.attributes?.[child.value] ?? null;
          }
          raw = group;
        }
        if (typeof f.parse === 'function') {
          form.value[f.value] = f.parse(raw, item);
          continue;
        }
        form.value[f.value] = raw ?? null;

        // OPTIMIZADO: Si ya tienes el objeto seleccionado, NO consultes al API.
        if (f.fetchOptions && remoteOptionsRegistry[f.fetchOptions]) {
          if (form.value[f.value] && typeof form.value[f.value] === 'object') {
            f.options = [form.value[f.value]];
          } else if (form.value[f.value] && typeof form.value[f.value] !== 'object') {
            f.options = [];
          } else {
            f.options = [];
          }
        }
      }
      loading.value = false;
    } catch (err) {
      console.error('[EditForm] Error al cargar config:', err);
    }
  }

  // ======= Watchers genéricos para dependencias ======
  allFields.value
    .filter(field => !!field.dependsOn)
    .forEach(field => {
      watch(
        () => form.value[field.dependsOn as string],
        async (newValue) => {
          let conditionMet = true;
          const condition = field.dependsCondition;
          switch (condition?.condition) {
            case 'diferent':
              conditionMet = newValue !== undefined && newValue !== '' && newValue !== condition.value;
              break;
            case 'equals':
              conditionMet = newValue === condition.value;
              break;
            case 'includes':
              conditionMet = !JSON.stringify(newValue)?.toLowerCase?.().includes(condition.value);
              break;
            default:
              conditionMet = !!newValue;
          }
        },
        { immediate: true }
      );
    });
  // ======= END Watchers dependencias ======
});


return (_ctx: any,_cache: any) => {
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_form = _resolveComponent("v-form")!

  return (_openBlock(), _createBlock(_component_v_form, {
    onSubmit: _withModifiers(submitForm, ["prevent"]),
    ref_key: "formRef",
    ref: formRef,
    modelValue: formValid.value,
    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((formValid).value = $event))
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_card, { class: "px-5 py-3" }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card_title, { class: "d-flex justify-space-between align-center mb-3" }, {
            default: _withCtx(() => [
              _createElementVNode("span", null, _toDisplayString(_unref(formTitle)), 1)
            ]),
            _: 1
          }),
          _createVNode(_component_v_card_text, { style: {"scrollbar-width":"none"} }, {
            default: _withCtx(() => [
              (loading.value)
                ? (_openBlock(), _createBlock(_component_v_row, {
                    key: 0,
                    class: "ma-12"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_spacer),
                      _createVNode(_component_v_progress_circular, { indeterminate: "" }),
                      _createVNode(_component_v_spacer)
                    ]),
                    _: 1
                  }))
                : (_openBlock(), _createBlock(_component_v_row, { key: 1 }, {
                    default: _withCtx(() => [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleFields.value, (field) => {
                        return (_openBlock(), _createBlock(_component_v_col, {
                          key: field.value,
                          cols: field.col || 12
                        }, {
                          default: _withCtx(() => [
                            (!field.isJsonArray)
                              ? (_openBlock(), _createBlock(_resolveDynamicComponent(field.component), _mergeProps({
                                  key: 0,
                                  modelValue: form.value[field.value],
                                  "onUpdate:modelValue": ($event: any) => ((form.value[field.value]) = $event)
                                }, { ref_for: true }, getFieldProps(field), {
                                  rules: field.required ? [v => !!v || 'Campo obligatorio'] : [],
                                  variant: "solo-filled"
                                }), null, 16, ["modelValue", "onUpdate:modelValue", "rules"]))
                              : (field.layoutOnly)
                                ? (_openBlock(), _createBlock(_resolveDynamicComponent(field.component), {
                                    key: 1,
                                    "model-value": form.value,
                                    "onUpdate:modelValue": _cache[0] || (_cache[0] = val => Object.assign(form.value, val)),
                                    fields: field.children,
                                    summary: field.summary,
                                    label: field.label,
                                    "layout-only": field.layoutOnly,
                                    form: form.value,
                                    variant: "solo-filled"
                                  }, null, 8, ["model-value", "fields", "summary", "label", "layout-only", "form"]))
                                : (_openBlock(), _createBlock(_resolveDynamicComponent(field.component), {
                                    key: 2,
                                    modelValue: form.value[field.value],
                                    "onUpdate:modelValue": ($event: any) => ((form.value[field.value]) = $event),
                                    fields: field.children,
                                    summary: field.summary,
                                    label: field.label,
                                    "layout-only": field.layoutOnly,
                                    form: form.value,
                                    variant: "solo-filled"
                                  }, null, 8, ["modelValue", "onUpdate:modelValue", "fields", "summary", "label", "layout-only", "form"]))
                          ]),
                          _: 2
                        }, 1032, ["cols"]))
                      }), 128))
                    ]),
                    _: 1
                  }))
            ]),
            _: 1
          }),
          _createVNode(_component_v_card_actions, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_spacer),
              _createVNode(_component_v_btn, {
                variant: "text",
                onClick: closeForm
              }, {
                default: _withCtx(() => _cache[4] || (_cache[4] = [
                  _createTextVNode("Cancelar")
                ])),
                _: 1,
                __: [4]
              }),
              (hasSteps.value && currentStep.value > 0)
                ? (_openBlock(), _createBlock(_component_v_btn, {
                    key: 0,
                    variant: "text",
                    onClick: _cache[1] || (_cache[1] = ($event: any) => (currentStep.value = currentStep.value - 1))
                  }, {
                    default: _withCtx(() => _cache[5] || (_cache[5] = [
                      _createTextVNode(" Anterior ")
                    ])),
                    _: 1,
                    __: [5]
                  }))
                : _createCommentVNode("", true),
              (hasSteps.value && currentStep.value < steps.value.length - 1)
                ? (_openBlock(), _createBlock(_component_v_btn, {
                    key: 1,
                    color: "primary",
                    variant: "tonal",
                    onClick: _cache[2] || (_cache[2] = ($event: any) => (currentStep.value = currentStep.value + 1))
                  }, {
                    default: _withCtx(() => _cache[6] || (_cache[6] = [
                      _createTextVNode(" Siguiente ")
                    ])),
                    _: 1,
                    __: [6]
                  }))
                : _createCommentVNode("", true),
              _createVNode(_component_v_btn, {
                type: "submit",
                disabled: !formValid.value || (hasSteps.value && currentStep.value < steps.value.length - 1),
                loading: is_saving.value,
                color: "primary"
              }, {
                default: _withCtx(() => _cache[7] || (_cache[7] = [
                  _createTextVNode("Guardar")
                ])),
                _: 1,
                __: [7]
              }, 8, ["disabled", "loading"])
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["modelValue"]))
}
}

})